/* OwnPathPreview.jsx — Path #7 full preview (USER view).
 *
 * Renders every field of the idea + every linked govt scheme (full record:
 * title/region/type/bestFor/supportTypes/keyFigures/documents/applyProcess
 * /officialGovernmentLinks/warning verbatim) + meta.disclaimer banner.
 *
 * Backend gate: /api/ownpath/idea/{id} returns the FULL idea + matched
 * schemes only when the user has kept this idea (or is admin). The 69
 * ideas + 55 schemes never hit the browser in bulk.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Quote, Wallet, ListChecks, CheckCircle2, Lightbulb,
  TrendingUp, ShieldAlert, Info, Sparkles, AlertTriangle,
  BookOpen, Target, Building2,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Section, InfoCard, ChipList, PillNav,
} from '../components/preview-ui/PreviewPrimitives';
import SchemeCard from '../components/SchemeCard';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'snapshot',    label: 'Snapshot',          icon: Sparkles },
  { id: 'who',         label: 'Who Should Choose', icon: CheckCircle2 },
  { id: 'first-steps', label: 'First Steps',       icon: ListChecks },
  { id: 'costing',     label: 'Costing & Pricing', icon: Wallet },
  { id: 'quiz',        label: 'Self-Check Quiz',   icon: Target },
  { id: 'myths',       label: 'Myths vs Reality',  icon: BookOpen },
  { id: 'risk',        label: 'Main Risk',         icon: ShieldAlert },
  { id: 'schemes',     label: 'Govt Schemes',      icon: Building2 },
];

export default function OwnPathPreview() {
  const { ideaId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [ideaId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(''); setData(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/ownpath/idea/${ideaId}`);
        if (!cancelled) setData(r.data || null);
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) setErr('This preview is being added.');
        else if (st === 403) setErr('Open this preview from your picks.');
        else if (st === 401) setErr('Your session expired — please reload the page.');
        else setErr(e?.response?.data?.detail || 'Could not load this preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ideaId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="op-preview-loading">
          <Loader2 className="w-10 h-10 text-rose-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="op-preview-error">
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {err || 'This preview is being added.'}
            </p>
            <Button asChild className="mt-4 bg-rose-600 hover:bg-rose-700">
              <Link to="/ownpath">Back to OwnPath</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const idea = data;
  const detail = idea.detail || {};
  const schemes = idea.matched_schemes || [];

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-rose-500 via-pink-500 to-amber-500 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/ownpath"
            data-testid="op-preview-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to OwnPath
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-bold">
            <span className="bg-white/25 backdrop-blur px-3 py-1 rounded-full" data-testid="op-preview-id">
              {String(idea.id).toUpperCase().replace(/-/g, ' ')}
            </span>
            {idea.category && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="op-preview-category">
                {idea.category}
              </span>
            )}
            {idea.budgetBand && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="op-preview-band">
                {idea.budgetBand}
              </span>
            )}
            {idea.mode && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full">{idea.mode}</span>
            )}
            {idea.difficulty && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full">{idea.difficulty}</span>
            )}
          </div>
          <h1
            data-testid="op-preview-title"
            className="text-3xl md:text-5xl font-black leading-tight mb-3"
          >
            {idea.title}
          </h1>
          {idea.startBudget && (
            <p className="text-lg md:text-xl text-white/90 font-semibold mb-5" data-testid="op-preview-startbudget">
              Start budget: {idea.startBudget}
            </p>
          )}
          {idea.demandLogic && (
            <blockquote
              className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-5 py-5 md:px-7 md:py-6"
              data-testid="op-preview-demand"
            >
              <Quote size={20} className="text-amber-200 mb-2.5" />
              <p className="text-base md:text-lg text-white leading-relaxed italic">{idea.demandLogic}</p>
            </blockquote>
          )}
        </div>
      </section>

      <PillNav sections={SECTIONS} testidPrefix="op-preview-nav" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Standing meta.disclaimer banner */}
        {idea.disclaimer && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 md:p-5"
            data-testid="op-preview-disclaimer"
          >
            <div className="flex items-start gap-3">
              <Info size={20} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] uppercase font-bold tracking-widest text-amber-800 mb-1">
                  Disclaimer
                </p>
                <p className="text-sm text-gray-800 leading-relaxed">{idea.disclaimer}</p>
              </div>
            </div>
          </div>
        )}

        {/* Snapshot — demandLogic + marginLogic */}
        <Section id="snapshot" icon={Sparkles} title="Snapshot" accent="rose">
          <div className="space-y-3">
            {idea.demandLogic && <InfoCard label="Demand logic" tone="rose">{idea.demandLogic}</InfoCard>}
            {idea.marginLogic && <InfoCard label="Margin logic" tone="mint">{idea.marginLogic}</InfoCard>}
            {detail.timeToTest && <InfoCard label="Time to test" tone="sky">{detail.timeToTest}</InfoCard>}
          </div>
        </Section>

        {/* Who Should Choose */}
        {idea.whoShouldChoose?.length > 0 && (
          <Section id="who" icon={CheckCircle2} title="Who Should Choose" accent="emerald">
            <ChipList items={idea.whoShouldChoose} tone="emerald" />
          </Section>
        )}

        {/* First Steps — numbered cards */}
        {idea.firstSteps?.length > 0 && (
          <Section id="first-steps" icon={ListChecks} title="First Steps" accent="teal">
            <div className="space-y-3">
              {idea.firstSteps.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-teal-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-extrabold text-sm flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <p className="flex-1 text-[15px] text-gray-800 leading-relaxed pt-1">{s}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Costing + Pricing */}
        {(detail.starterCosts?.length > 0 || detail.pricing || idea.profitReality) && (
          <Section id="costing" icon={Wallet} title="Costing & Pricing" accent="amber">
            {detail.starterCosts?.length > 0 && (
              <div className="overflow-x-auto -mx-2 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase font-bold tracking-widest text-amber-800 border-b border-amber-200">
                      <th className="py-2 px-2">Item</th>
                      <th className="py-2 px-2 text-right">Cost (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.starterCosts.map((c, i) => (
                      <tr key={i} className="border-b border-amber-100 last:border-0">
                        <td className="py-2 px-2 text-gray-800">{c[0]}</td>
                        <td className="py-2 px-2 text-right font-semibold text-gray-900">{c[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {detail.pricing && (
              <InfoCard label="Pricing guide" tone="cream">{detail.pricing}</InfoCard>
            )}
            {idea.profitReality && (
              <div className="mt-4" data-testid="op-profit-reality">
                <p className="text-[11px] uppercase font-bold tracking-widest text-amber-800 mb-2">Profit reality (estimates, not guarantees)</p>
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      ['Est. monthly revenue', idea.profitReality.estMonthlyRevenue],
                      ['Est. monthly costs', idea.profitReality.estMonthlyCosts],
                      ['Est. monthly profit', idea.profitReality.estMonthlyProfit],
                      ['Typical break-even', idea.profitReality.typicalBreakEven],
                    ].filter(([, v]) => v).map(([label, v], i) => (
                      <tr key={i} className="border-b border-amber-100 last:border-0">
                        <td className="py-2 px-2 text-gray-800">{label}</td>
                        <td className="py-2 px-2 text-right font-semibold text-gray-900">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {idea.profitReality.note && (
                  <p className="text-xs text-gray-500 italic mt-2">{idea.profitReality.note}</p>
                )}
              </div>
            )}
          </Section>
        )}

        {/* Self-check quiz — 3-column cards */}
        {detail.selfCheckQuiz?.length > 0 && (
          <Section id="quiz" icon={Target} title="Self-Check Quiz" accent="purple">
            <div className="space-y-3">
              {detail.selfCheckQuiz.map((q, i) => (
                <div key={i} className="bg-white rounded-2xl border border-violet-200 p-4">
                  <p className="text-[15px] font-bold text-gray-900 mb-2">Q{i + 1}. {q[0]}</p>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                      <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-700">If yes</span>
                      <p className="text-gray-800 mt-1">{q[1]}</p>
                    </div>
                    <div className="rounded-xl bg-rose-50 border border-rose-200 p-3">
                      <span className="text-[10px] uppercase font-bold tracking-widest text-rose-700">If no</span>
                      <p className="text-gray-800 mt-1">{q[2]}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Myths vs reality */}
        {detail.myths?.length > 0 && (
          <Section id="myths" icon={BookOpen} title="Myths vs Reality" accent="indigo">
            <div className="grid md:grid-cols-2 gap-3">
              {detail.myths.map((m, i) => (
                <div key={i} className="rounded-2xl border border-indigo-200 bg-white p-4">
                  <p className="text-[11px] uppercase font-bold tracking-widest text-rose-700 mb-1">Myth</p>
                  <p className="text-sm text-gray-800 italic mb-2">"{m[0]}"</p>
                  <p className="text-[11px] uppercase font-bold tracking-widest text-emerald-700 mb-1">Reality</p>
                  <p className="text-sm text-gray-800">{m[1]}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Main risk */}
        {idea.mainRisk && (
          <Section id="risk" icon={ShieldAlert} title="Main Risk" accent="rose">
            <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle size={22} className="text-rose-600 shrink-0 mt-0.5" />
                <p className="text-[15px] text-gray-800 leading-relaxed">{idea.mainRisk}</p>
              </div>
            </div>
          </Section>
        )}

        {/* Government schemes — FULL records */}
        {schemes.length > 0 && (
          <Section id="schemes" icon={Building2} title="Government Schemes You Can Use" accent="emerald">
            <p className="text-sm text-gray-600 mb-4">
              These schemes are matched from this idea&apos;s <code className="bg-gray-100 rounded px-1.5 py-0.5 text-xs">schemesToUse</code> list,
              filtered to your state ({idea.user_state === 'andhraPradesh' ? 'Andhra Pradesh' : idea.user_state === 'telangana' ? 'Telangana' : 'your region'})
              plus central schemes. Every line is rendered verbatim from the file.
            </p>
            <div className="space-y-4">
              {schemes.map((s) => (
                <SchemeCard key={s.id} scheme={s} defaultOpen testidPrefix="op-scheme" />
              ))}
            </div>
          </Section>
        )}

      </main>

      <Footer />
    </div>
  );
}
